import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AngularMaterialModule } from './angular-material.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CoreModule } from './_core/core.module';

import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FooterComponent } from "./shared/components/footer/footer.component";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { CacheInterceptor } from './_core/interceptors/data-source-cache.interceptor';
import { RequestGroupingInterceptor } from './_core/interceptors/request-grouping.interceptor';
import { BusinessContextInterceptor } from './_core/interceptors/business-context.interceptor';
import { BusinessScopingRegistry } from './_core/interceptors/business-scoping-map';
import { BUSINESS_SCOPING_ENTRIES } from './_core/interceptors/business-scoping.config';
import { BusinessContextService } from './_core/state/business';
import { provideTippyLoader, provideTippyConfig, tooltipVariation, popperVariation } from '@ngneat/helipopper/config';
import { SharedCoreModule } from './shared/shared-core.module';
import { GlobalUploadStatusBarComponent } from './shared/components/global-upload-status-bar/global-upload-status-bar.component';
import { AgentTransitionOverlayComponent } from './shared/ai/agent/transition/agent-transition-overlay.component';


@NgModule({
	declarations: [
		AppComponent
	],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		AngularMaterialModule,
		HttpClientModule,
		CoreModule,
		FormsModule,
		ReactiveFormsModule,
		FooterComponent,
		SharedCoreModule,
		GlobalUploadStatusBarComponent,
		AgentTransitionOverlayComponent
	],
	providers: [
		provideTippyLoader(() => import('tippy.js')),
		provideTippyConfig({
			defaultVariation: 'tooltip',
			variations: {
				tooltip: tooltipVariation,
				popper: popperVariation
			}
		}),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false
			})
		},
		{
			provide: Sentry.TraceService,
			deps: [Router]
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (businessContext: BusinessContextService) => () => businessContext.initializeActiveBusiness(),
			deps: [BusinessContextService],
			multi: true
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (registry: BusinessScopingRegistry) => () => registry.registerAll(BUSINESS_SCOPING_ENTRIES),
			deps: [BusinessScopingRegistry],
			multi: true
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true
		},
		{
			provide: MAT_DATE_LOCALE, useValue: "zn-CH"
		},
		{
			provide: DateAdapter,
			useClass: DateFnsAdapter,
			deps: [MAT_DATE_LOCALE]
		},
		{
			provide: MAT_DATE_FORMATS,
			useValue: MAT_DATE_FNS_FORMATS
		},
		{
			// Registered ahead of grouping/cache so the injected business id is
			// part of their dedup/cache keys (no stale cross-business hits).
			provide: HTTP_INTERCEPTORS,
			useClass: BusinessContextInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: RequestGroupingInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: CacheInterceptor,
			multi: true,
		}
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
